import type { TableNode } from '@table-library/react-table-library/types/table'
import { ReactNode, useContext } from 'react'
import { useTranslation } from 'libs/i18n'
import { Currency, TableImage } from 'shared/components'
import { format, parseISO } from 'libs/dates'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import type { SaleItem } from 'features/sales/types/sales.types'
import type { CardTableColumn } from 'styles/table'

const cell = (render: (sale: SaleItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as SaleItem)

const numeric = 'block text-right tabular-nums'
const muted = 'block text-right tabular-nums text-muted-foreground'

export const useColumns = (): CardTableColumn[] => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)

	return [
		{
			label: t('Product'),
			width: 'minmax(240px, 1.6fr)',
			renderCell: cell((item) => (
				<div className="flex items-center gap-3">
					<TableImage src={item.product_image} />
					<div className="min-w-0 flex-1">
						<span className="block truncate text-[13px] font-semibold text-primary">
							{item.product_name}
						</span>
						<span className="block truncate text-[11px] text-muted-foreground">
							{item.product_sku}
						</span>
					</div>
				</div>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Date'),
			width: '110px',
			renderCell: cell((item) => (
				<span className="block whitespace-nowrap tabular-nums">
					<span className="block font-medium">
						{format(parseISO(String(item.created)), 'dd.MM.yyyy')}
					</span>
					<span className="block text-muted-foreground">
						{format(parseISO(String(item.created)), 'HH:mm:ss')}
					</span>
				</span>
			))
		},
		{
			label: t('Reference'),
			width: '100px',
			renderCell: cell((item) => (
				<span className="block tabular-nums text-muted-foreground">{item.receipt_reference}</span>
			))
		},
		...(isStaff
			? [
					{
						label: t('User'),
						width: 'minmax(170px, 1fr)',
						renderCell: cell((item) => (
							<span className="block leading-snug">
								<span className="block truncate font-medium">{item.account_name}</span>
								<span className="block truncate text-[11px] text-muted-foreground">
									{item.account_email}
								</span>
							</span>
						))
					}
				]
			: []),
		{
			label: t('Booking'),
			width: 'minmax(160px, 1fr)',
			renderCell: cell((item) =>
				item.booking?.id ? (
					<span className="block leading-snug">
						<span className="block whitespace-nowrap font-medium">
							{item.booking.booth?.booth_type?.name}
							{item.booking.booth?.number ? ` #${item.booking.booth.number}` : ''}
						</span>
						<span className="block whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
							#{item.booking.reference} {item.booking.start_date}
						</span>
					</span>
				) : (
					<span className="block text-muted-foreground">—</span>
				)
			)
		},
		{
			label: t('Quantity'),
			width: '90px',
			align: 'right',
			renderCell: cell((item) => <span className={numeric}>{item.quantity}</span>)
		},
		{
			label: t('Price'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.product_unit_price} />)
		},
		{
			label: t('Subtotal'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.subtotal} />)
		},
		...(storeCtx?.storeSettings?.vat_enabled
			? [
					{
						label: t('VAT amount'),
						width: '110px',
						align: 'right' as const,
						renderCell: cell((item) => <Currency className={muted} price={item.vat} />)
					},
					{
						label: t('VAT %'),
						width: '80px',
						align: 'right' as const,
						renderCell: cell((item) => (
							<span className={muted}>{item.vat_percentage ? `${item.vat_percentage}%` : '—'}</span>
						))
					}
				]
			: []),
		{
			label: t('Discount'),
			width: '100px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.discount} />)
		},
		{
			label: t('Total'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency
					className="block text-right font-display text-[13px] font-semibold tabular-nums"
					price={item.total}
				/>
			))
		},
		{
			label: t('Commission'),
			width: '130px',
			align: 'right',
			renderCell: cell((item) => (
				<span className={numeric}>
					<Currency price={item.commission} />{' '}
					<span className="text-muted-foreground">({+item.commission_percentage}%)</span>
				</span>
			))
		},
		{
			label: t('Net profit'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency
					className="block text-right font-semibold tabular-nums text-success"
					price={{ amount: Number(item.net_profit), currency: item.total.currency }}
				/>
			))
		},
		{
			label: t('Payout ID'),
			width: '90px',
			align: 'right',
			renderCell: cell((item) => (
				<span className={muted}>{item.payout ? `#${item.payout}` : '—'}</span>
			))
		}
	]
}
